import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/context/AuthContext';
import Layout from '@components/Layout';
import { db, storage } from '@root/firebase'; // Import your Firebase config
import {
  doc,
  getDoc,
  setDoc,
  collection,
  query,
  orderBy,
  limit,
  getDocs,
  where,
  updateDoc,
  deleteField,
} from 'firebase/firestore';

import MemorialMenu from '@components/MemorialMenu';
import { myMemorialMenu, createMemorialMenu, memorialMenu } from '@utils/Index';

const header1 =
  'https://firebasestorage.googleapis.com/v0/b/smartdog-bbe9f.firebasestorage.app/o/images%2Fdogs%2Fdog.jpg?alt=media&token=a3d4c7ef-7475-480a-9316-05112b71e7d1';
const header2 =
  'https://firebasestorage.googleapis.com/v0/b/smartdog-bbe9f.firebasestorage.app/o/images%2Fdogs%2Fdog2.jpg?alt=media&token=dce8b4c7-57f7-473a-8e36-b057ca149ee9';
const header3 =
  'https://firebasestorage.googleapis.com/v0/b/smartdog-bbe9f.firebasestorage.app/o/images%2Fdogs%2Fdog3.jpg?alt=media&token=a1b3eded-682f-4581-91a7-5f72e0490dee';

//import header1 from "@images/landscape/dog.jpg"
//import header2 from "@images/landscape/dog2.jpg"
//import header3 from "@images/landscape/dog3.jpg"

import img1 from '@images/landscape/dog.jpg';
import img2 from '@images/landscape/dog2.jpg';

import Welcome from './content/Welcome';
import CreateTribute from './content/CreateTribute';
import CreateEvent from './content/CreateEvent';
import MyMemorial from './content/MyMemorials';
import FavoritedMemorials from './content/FavoritedMemorials';
import MemorialSearch from './content/MemorialSearch';
import CommunitySupport from './content/CommunitySupport';

function Memorial() {
  const { globalUser, logout } = useAuth();
  const headers = [header1, header2, header3];
  const [selectedMenu, setSelectedMenu] = useState({
    key: 'welcome',
    value: '',
  });
  const [tributeCount, setTributeCount] = useState(0);
  const [showPictureHeader, setShowPictureHeader] = useState(true);
  const [breadcrumbTitle, setBreadcrumbTitle] = useState('Memorial');

  useEffect(() => {
    //console.log("Tribute count updated:", tributeCount); // Confirm state updates
  }, [tributeCount]);

  useEffect(() => {
    (async () => {
      if (!globalUser?.uid) return;
      try {
        const memorialsQuery = query(
          collection(db, 'memorials'),
          where('uid', '==', globalUser.uid.trim())
        );
        const snapshot = await getDocs(memorialsQuery);
        setTributeCount(snapshot.size || 0);
      } catch (e) {
        console.error('Error fetching user tributes:', e);
        setTributeCount(0);
      }
    })();
  }, [globalUser?.uid]);

  const handleSwitch = () => {
    const contentMap = {
      welcome: <Welcome />,
      // Pass menuValue here
      createMemorial: (
        <CreateTribute
          menuValue={selectedMenu.value}
          setTributeCount={setTributeCount}
        />
      ),
      createEvent: (
        <CreateEvent
          menuValue={selectedMenu.value}
          setTributeCount={setTributeCount}
        />
      ),
      myMemorial: <MyMemorial setTributeCount={setTributeCount} />,
      searchMemorial: <MemorialSearch />,
      community: <CommunitySupport />,
    };

    return contentMap[selectedMenu.key];
  };

  // Build sections for the memorial sidebar (parent/children structure like Marketplace)
  const sections = useMemo(() => {
    const myChildren = myMemorialMenu.map((item) => ({
      id: item.link,
      label: item.value,
      link: item.link,
      icon: item.icon,
      // show count only on the "myMemorial" child
      count: item.link === 'myMemorial' ? tributeCount : 0,
    }));
    const myTotal = myChildren.reduce((sum, c) => sum + (c.count || 0), 0);

    const createChildren = createMemorialMenu.map((item) => ({
      id: item.link,
      label: item.value,
      link: item.link,
      icon: item.icon,
      count: 0,
    }));

    const otherChildren = memorialMenu.map((item) => ({
      id: item.link,
      label: item.value,
      link: item.link,
      icon: item.icon,
      count: 0,
    }));

    return [
      {
        id: 'my',
        name: 'My Memorials',
        totalCount: myTotal,
        children: myChildren,
      },
      {
        id: 'create',
        name: 'Create a Memorial',
        totalCount: 0,
        children: createChildren,
      },
      {
        id: 'other',
        name: 'Other',
        totalCount: 0,
        children: otherChildren,
      },
    ];
  }, [tributeCount]);

  const pageMeta = {
    welcome: { title: 'Memorial', showHeader: true },
    createMemorial: { title: 'Create a Memorial', showHeader: false },
    createEvent: { title: 'Create an Event', showHeader: false },
    myMemorial: { title: 'My Memorials', showHeader: false },
    searchMemorial: { title: 'Search Memorials', showHeader: false },
    community: { title: 'Community Support', showHeader: false },
  };

  const handleSelect = (child) => {
    // child: { id, label, link, count }
    setSelectedMenu({ key: child.link, value: child.label });

    const meta = pageMeta[child.link] || {
      title: child.label || 'Memorial',
      showHeader: false,
    };

    setBreadcrumbTitle(meta.title);
    setShowPictureHeader(meta.showHeader);
  };

  return (
    <Layout>
      <div className='row page-titles'>
        <div className='col-lg-5'>
          <ol className='breadcrumb'>
            <li className='breadcrumb-item active'>Features</li>
            <li className='breadcrumb-item'>{breadcrumbTitle}</li>
          </ol>
        </div>
        <div className='col-lg-4'></div>
      </div>

      {showPictureHeader && (
        <div className='col-12'>
          <div className='card-deck-wrapper'>
            <div className='card-group'>
              {headers.map((src, idx) => (
                <div className='card mb-4' key={idx}>
                  <img
                    className='card-img-bottom card-img-top img-fluid'
                    src={src}
                    alt={`Card image ${idx + 1}`}
                    onError={(e) =>
                      (e.currentTarget.src = '/images/default.jpg')
                    }
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className='row'>
        {/* Sidebar */}
        <div className='col-lg-3'>
          <div className='card'>
            <h5 className='card-header bg-transparent border-bottom'>
              Memorial Quicklinks
            </h5>
            <div className='card-body'>
              <MemorialMenu sections={sections} onItemSelect={handleSelect} />
            </div>
          </div>
        </div>
        {/* End Left Side Menu Bar */}

        {handleSwitch()}
      </div>
    </Layout>
  );
}

export default Memorial;
